/* ============================================================
   The toast — one calm line at the bottom, auto-dismissing. Where the
   celebration copy lands ("Shine", in Fogg's word): a rep is acknowledged the
   moment it's logged, in a sentence rather than a score.
   ============================================================ */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface ToastValue {
  toast: (text: string, opts?: { emoji?: string; ms?: number }) => void;
}
const Ctx = createContext<ToastValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [cur, setCur] = useState<{ id: number; text: string; emoji?: string; ms: number } | null>(null);
  const toast = useCallback<ToastValue['toast']>((text, opts) => {
    setCur({ id: Date.now(), text, emoji: opts?.emoji, ms: opts?.ms ?? 2600 });
  }, []);
  useEffect(() => {
    if (!cur) return;
    const t = setTimeout(() => setCur(null), cur.ms);
    return () => clearTimeout(t);
  }, [cur]);
  const value = useMemo(() => ({ toast }), [toast]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {cur ? (
        <div key={cur.id} role="status" className="toast-in pointer-events-none fixed bottom-[calc(92px+env(safe-area-inset-bottom))] left-1/2 z-40 max-w-[calc(100vw-32px)] md:bottom-7 flex -translate-x-1/2 items-center gap-2.5 rounded-full bg-toast py-2.5 pl-3 pr-5 text-[14px] font-semibold text-white shadow-pop">
          {cur.emoji ? <span className="sun-gradient flex h-7 w-7 items-center justify-center rounded-full text-[15px]">{cur.emoji}</span> : null}
          <span>{cur.text}</span>
        </div>
      ) : null}
    </Ctx.Provider>
  );
}

export function useToast(): ToastValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast must be used within ToastProvider');
  return v;
}

/* Celebration copy — identity first, numbers never. Rotates so the tenth rep
   doesn't read like the first. */
const SHINE = [
  'Rep logged. That’s who you are.',
  'Showed up. That’s the whole game.',
  'Small, done, counted.',
  'Another brick. Nicely laid.',
  'Kept the promise to yourself.',
  'That’s the kind of person you’re becoming.',
];
export function shineLine(seed: number): string {
  return SHINE[Math.abs(seed) % SHINE.length]!;
}
